import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlayerAuctionService } from '../player-auction.service';
import { LoginInfo } from '../../login-info/login-info.type';
import { EmailLoginField } from '../../shared/components/email-login-field/email-login-field';
import { AuthService } from '../../auth/auth.service';
import { PROXY_COUNTRIES } from '../../shared/data/proxy-countries';

@Component({
  selector: 'app-sell-contract-form-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatSelectModule,
    EmailLoginField
  ],
  templateUrl: './sell-contract-form-dialog.html',
  styleUrl: './sell-contract-form-dialog.scss'
})
export class SellContractFormDialog {
  form: FormGroup;
  loading = false;

  loginInfoEmailControl = new FormControl<string>('', { nonNullable: true });
  selectedLoginInfo: LoginInfo | null = null;
  readonly countries = PROXY_COUNTRIES;

  constructor(
    private fb: FormBuilder,
    private service: PlayerAuctionService,
    private dialogRef: MatDialogRef<SellContractFormDialog>,
    private snackBar: MatSnackBar,
    private authService: AuthService,
  ) {
    this.form = this.fb.group({
      name: [''],
      note: [''],
      target_coins: [null, [Validators.required, Validators.min(1)]],
      fee: [0, [Validators.min(0), Validators.max(10000000)]],
      target_type: ['received'],
      in_pool: [true],
      country: [''],
    });
  }

  get isAdmin(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  onLoginInfoChange(loginInfo: LoginInfo | null): void {
    this.selectedLoginInfo = loginInfo;
  }

  isAccountSelected(): boolean {
    return !!this.selectedLoginInfo;
  }

  onSubmit(): void {
    if (this.form.invalid || !this.selectedLoginInfo) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    const { target_coins, fee, name, note, target_type, in_pool, country } = this.form.value;
    this.service.createSellContract({
      name,
      note,
      account_email: this.selectedLoginInfo.email,
      target_coins: target_coins * 1000,
      fee: this.isAdmin ? fee : 0,
      target_type,
      in_pool,
      country: country || undefined,
    }).subscribe({
      next: () => {
        this.snackBar.open('Sell contract created', 'Close', { duration: 4000 });
        this.dialogRef.close(true);
      },
      error: err => {
        const msg = err.error?.error || 'Error creating sell contract';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  getErrorMessage(field: string): string {
    const c = this.form.get(field);
    if (!c) return '';
    if (c.hasError('required')) return 'This field is required';
    if (c.hasError('min')) return `Minimum value is ${c.errors?.['min'].min}`;
    return '';
  }
}
